import { useState } from 'react';
import type { Practice, Settings } from '../types';
import { Chips, Sheet, Stepper, Switch } from '../ui/parts';
import { buildTimeline, clamp, formatLength, MAX_POSE_SECONDS, MIN_POSE_SECONDS } from '../lib/timeline';
import { resetSettings } from '../lib/storage';
import styles from './screens.module.css';

const MUSIC: { value: string; label: string }[] = [
  { value: 'ambient-part1', label: 'Ambient 1 (20 min)' },
  { value: 'ambient-part2', label: 'Ambient 2 (20 min)' },
  { value: 'ambient-part3', label: 'Ambient 3 (8 min)' },
  { value: 'krishna_flute', label: 'Krishna flute' },
  { value: 'none', label: 'No music' },
];

const PRESETS = [5, 12, 30, 60, 120, 300];
const presetLabel = (s: number) => (s < 60 ? `${s}s` : `${s / 60}m`);

const stepFor = (v: number, dir: 1 | -1) => {
  const edge = dir < 0 ? v - 1 : v;
  return edge < 60 ? 5 : edge < 300 ? 15 : 60;
};

const nudge = (v: number, dir: 1 | -1) => {
  const step = stepFor(v, dir);
  const next = dir > 0 ? (Math.floor(v / step) + 1) * step : (Math.ceil(v / step) - 1) * step;
  return clamp(next, MIN_POSE_SECONDS, MAX_POSE_SECONDS);
};

type Mode = 'rounds' | 'pose' | 'length';

interface Props {
  practice: Practice;
  settings: Settings;
  onChange: (patch: Partial<Settings>) => void;
  onReset: () => void;
  onClose: () => void;
}

const Section = ({ title, children, open }: { title: string; children: React.ReactNode; open?: boolean }) => (
  <details className={styles.section} open={open}>
    <summary>{title}</summary>
    <div className={styles.sectionBody}>{children}</div>
  </details>
);

export const Customize = ({ practice, settings, onChange, onReset, onClose }: Props) => {
  const hasPoses = practice.steps.some(s => s.kind === 'pose');
  const isTrack = practice.steps.some(s => s.kind === 'track');
  const isTimer = practice.steps.some(s => s.kind === 'timer') || practice.free;
  const [mode, setMode] = useState<Mode>(practice.roundsAdjustable ? 'rounds' : 'pose');

  const timeline = buildTimeline(practice, settings);
  const oneRound = buildTimeline(practice, { ...settings, rounds: 1 }).total;
  const poseSecondsLabel =
    settings.secondsPerPose !== null
      ? `${settings.secondsPerPose >= 60 ? formatLength(settings.secondsPerPose) : `${settings.secondsPerPose}s`}`
      : 'Default pacing';

  const setPose = (s: number | null) => onChange({ secondsPerPose: s === null ? null : clamp(Math.round(s), MIN_POSE_SECONDS, MAX_POSE_SECONDS) });

  const modes: { value: Mode; label: string }[] = practice.roundsAdjustable
    ? [
        { value: 'rounds', label: 'Rounds' },
        { value: 'pose', label: 'Time per pose' },
        { value: 'length', label: 'Length' },
      ]
    : [{ value: 'pose', label: 'Time per pose' }];

  return (
    <Sheet title="Customize" onClose={onClose}>
      <p className={styles.sheetLead}>
        {practice.title}. Changes are saved for next time.
      </p>

      {!isTrack && (
        <Section title="Timing" open>
          <p className={styles.derived}>
            Total <strong>{formatLength(timeline.total)}</strong>
          </p>

          {practice.free && (
            <>
              <label className={styles.fieldLabel}>Length</label>
              <Stepper
                label="length"
                value={settings.minutes ?? 20}
                display={`${settings.minutes ?? 20} min`}
                onMinus={() => onChange({ minutes: clamp((settings.minutes ?? 20) - 5, 5, 120) })}
                onPlus={() => onChange({ minutes: clamp((settings.minutes ?? 20) + 5, 5, 120) })}
              />
              <label className={styles.fieldLabel}>Bell every</label>
              <Chips
                label="Bell interval"
                value={settings.bellInterval}
                onChange={v => onChange({ bellInterval: v })}
                options={[30, 60, 120, 300, 600].map(v => ({ value: v, label: presetLabel(v) }))}
              />
            </>
          )}

          {!practice.free && isTimer && !hasPoses && (
            <>
              <label className={styles.fieldLabel}>Length</label>
              <Stepper
                label="length"
                value={settings.minutes ?? 5}
                display={`${settings.minutes ?? 5} min`}
                onMinus={() => onChange({ minutes: clamp((settings.minutes ?? 5) - 1, 1, 60) })}
                onPlus={() => onChange({ minutes: clamp((settings.minutes ?? 5) + 1, 1, 60) })}
              />
            </>
          )}

          {hasPoses && (
            <>
              {modes.length > 1 && (
                <>
                  <label className={styles.fieldLabel}>Set by</label>
                  <Chips label="Timing mode" value={mode} onChange={setMode} options={modes} />
                </>
              )}

              <div className={mode === 'rounds' ? '' : styles.readonly}>
                <label className={styles.fieldLabel}>Rounds {mode !== 'rounds' && '(calculated)'}</label>
                {practice.roundsAdjustable ? (
                  <Stepper
                    label="rounds"
                    value={settings.rounds}
                    display={`${settings.rounds}`}
                    onMinus={() => mode === 'rounds' && onChange({ rounds: clamp(settings.rounds - 1, 1, 99) })}
                    onPlus={() => mode === 'rounds' && onChange({ rounds: clamp(settings.rounds + 1, 1, 99) })}
                  />
                ) : (
                  <p className={styles.derived}>1 round</p>
                )}
              </div>

              <div className={mode === 'pose' ? '' : styles.readonly}>
                <label className={styles.fieldLabel}>Time per pose {mode !== 'pose' && '(calculated)'}</label>
                <Stepper
                  label="time per pose"
                  value={settings.secondsPerPose ?? 0}
                  display={poseSecondsLabel}
                  onMinus={() => mode === 'pose' && setPose(nudge(settings.secondsPerPose ?? 30, -1))}
                  onPlus={() => mode === 'pose' && setPose(nudge(settings.secondsPerPose ?? 30, 1))}
                />
                {mode === 'pose' && (
                  <>
                    <Chips
                      label="Time per pose presets"
                      value={settings.secondsPerPose}
                      onChange={v => setPose(v)}
                      options={PRESETS.map(v => ({ value: v, label: presetLabel(v) }))}
                    />
                    {settings.secondsPerPose !== null && (
                      <button className="text-button" onClick={() => setPose(null)}>
                        Use the routine's own pacing
                      </button>
                    )}
                  </>
                )}
              </div>

              {mode === 'length' && practice.roundsAdjustable && (
                <div>
                  <label className={styles.fieldLabel}>Length</label>
                  <Stepper
                    label="length"
                    value={Math.round(timeline.total / 60)}
                    display={formatLength(timeline.total)}
                    onMinus={() => onChange({ rounds: clamp(Math.round(((timeline.total / 60 - 2) * 60) / oneRound), 1, 99) })}
                    onPlus={() => onChange({ rounds: clamp(Math.round(((timeline.total / 60 + 2) * 60) / oneRound), 1, 99) })}
                  />
                </div>
              )}
            </>
          )}
        </Section>
      )}

      <Section title="Sound">
        {!isTrack && (
          <>
            <label className={styles.fieldLabel} htmlFor="music">Background music</label>
            <select id="music" value={settings.music} onChange={e => onChange({ music: e.target.value })}>
              {MUSIC.map(m => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </>
        )}
        <label className={styles.fieldLabel} htmlFor="music-vol">Music volume</label>
        <input id="music-vol" type="range" min={0} max={100} value={Math.round(settings.musicVolume * 100)} onChange={e => onChange({ musicVolume: Number(e.target.value) / 100 })} />
        {!isTrack && (
          <>
            <label className={styles.fieldLabel} htmlFor="bell-vol">Bell volume</label>
            <input id="bell-vol" type="range" min={0} max={100} value={Math.round(settings.bellVolume * 100)} onChange={e => onChange({ bellVolume: Number(e.target.value) / 100 })} />
          </>
        )}
        {hasPoses && <Switch label="Say the pose name aloud" checked={settings.voice} onChange={v => onChange({ voice: v })} />}
      </Section>

      {hasPoses && (
        <Section title="Display">
          <Switch label="Show pose names" checked={settings.showNames} onChange={v => onChange({ showNames: v })} />
        </Section>
      )}

      <button
        className="text-button"
        onClick={() => {
          resetSettings(practice.id);
          onReset();
        }}
      >
        Reset to recommended
      </button>
    </Sheet>
  );
};
